import { useCallback, useEffect, useState } from "react";
import type { SessionInfo } from "./api.js";
import { cachedSession, currentSession, forgetCachedSession } from "./api.js";
import { navigate, useRoute } from "./router.js";
import { Masthead } from "./components/masthead.js";
import { Landing } from "./pages/landing.js";
import { Login } from "./pages/login.js";
import { Dashboard } from "./pages/dashboard.js";
import { Databases, DatabaseDetail } from "./pages/databases.js";
import { Managed } from "./pages/managed.js";
import { ManagedDocs } from "./pages/managed-docs.js";
import { Workspace } from "./pages/workspace.js";
import { Settings } from "./pages/settings.js";
import { CliApproval } from "./pages/cli-approval.js";
import { Assistant } from "./pages/assistant.js";
import { Stats } from "./pages/stats.js";
import { Admin } from "./pages/admin.js";
import { Pricing } from "./pages/pricing.js";
import { Metrics } from "./pages/metrics.js";
import { Docs } from "./pages/docs.js";
import { Privacy } from "./pages/privacy.js";
import { Terms } from "./pages/terms.js";
import { Dmca } from "./pages/dmca.js";

export type Session = SessionInfo | null;

export function App() {
  const path = useRoute();
  // Boot from the cached answer so a repeat visit paints immediately; the
  // probe then revalidates in the background and corrects it if it changed.
  const [session, setSession] = useState<Session>(cachedSession);
  const [loading, setLoading] = useState(() => cachedSession() === null);

  const refresh = useCallback(async () => {
    try {
      setSession(await currentSession());
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="shell">
      <Masthead
        session={session}
        onSignOut={() => {
          forgetCachedSession();
          setSession(null);
        }}
      />
      {loading ? <div className="page-loading meta">Loading…</div> : page(path, session, refresh)}
      <footer className="colophon meta">
        <span>saucer.sh</span>
        <span className="colophon__links">
          <a
            href="/docs"
            onClick={(event) => {
              event.preventDefault();
              navigate("/docs");
            }}
          >
            Docs
          </a>
          <a
            href="/privacy"
            onClick={(event) => {
              event.preventDefault();
              navigate("/privacy");
            }}
          >
            Privacy
          </a>
          <a
            href="/terms"
            onClick={(event) => {
              event.preventDefault();
              navigate("/terms");
            }}
          >
            Terms
          </a>
          <a
            href="/dmca"
            onClick={(event) => {
              event.preventDefault();
              navigate("/dmca");
            }}
          >
            DMCA
          </a>
          <a href="https://github.com/bas3line/saucer">Source</a>
        </span>
      </footer>
    </div>
  );
}

function page(path: string, session: Session, refresh: () => Promise<void>) {
  // The one route with a parameter. A full router would be more moving parts
  // than fourteen static pages and this warrant.
  const workspace = path.match(/^\/workspaces\/([a-z0-9]+)$/i);
  if (workspace) {
    return session ? <Workspace workspaceId={workspace[1]!} /> : <Login />;
  }

  const database = path.match(/^\/databases\/([a-z0-9]+)$/i);
  if (database) {
    return session ? (
      <DatabaseDetail session={session} databaseId={database[1]!} />
    ) : (
      <Login />
    );
  }

  switch (path) {
    case "/dashboard":
      return session ? <Dashboard session={session} onChange={refresh} /> : <Login />;
    case "/databases":
      return session ? <Databases session={session} /> : <Login />;
    // Public: it is the product's front door, not a signed-in page.
    case "/managed":
    case "/clickhouse":
      return <Managed session={session} />;
    case "/managed/docs":
      return <ManagedDocs />;
    case "/settings":
      return session ? <Settings session={session} /> : <Login />;
    case "/admin":
      // Not a secret — the API is what enforces this — just not worth a page
      // of "forbidden" for anyone who guesses the path.
      return session?.user.isAdmin ? <Admin /> : <Login />;
    case "/assistant":
      return session ? <Assistant session={session} /> : <Login />;
    case "/stats":
      return session ? <Stats /> : <Login />;
    case "/home":
      return <Landing />;
    case "/pricing":
      return <Pricing />;
    case "/metrics":
      return <Metrics />;
    case "/docs":
      return <Docs />;
    case "/privacy":
      return <Privacy />;
    case "/terms":
      return <Terms />;
    case "/dmca":
      return <Dmca />;
    case "/cli":
      return <CliApproval session={session} />;
    case "/login":
      return session ? <Dashboard session={session} onChange={refresh} /> : <Login />;
    default:
      return session ? <Dashboard session={session} onChange={refresh} /> : <Landing />;
  }
}
